import { useMemo } from "react";
import { useSelector } from "react-redux";

// Single access point for the proposal form's blueprint, now published by the
// backend (publishedformlayout / publishedformfields / publishedrules on
// cr_viewmaster, fetched by getFormRenderer) instead of the hard-coded blobs
// that used to live in selectedDetails/JSON.js.
//
// The admin panel reads the same three columns straight into its renderer
// (Order/Orders/SelectedOrders/ProposalForm.js). This app needs a hook on top
// because it also drives its OWN Previous/Next/Finish buttons, which have to
// walk the layout for per-step field names — something the admin panel gets
// from the library's built-in nav instead.
//
// Being a hook is itself the point. The old consumers read JSON.js at module
// load and froze the derived lookups into module-level consts. That is correct
// for a static import and wrong for a fetch: at module-evaluation time the
// response has not arrived, so those consts would freeze empty forever. Every
// derivation therefore has to live inside a hook and re-run when the blueprint
// lands.

// Layout nodes that only wrap other nodes; real inputs sit at the leaves. Kept
// in sync with the renderer's own extractFieldNames.
const CONTAINER_TYPES = [
  "columns",
  "column",
  "group",
  "field-set",
  "relation-accordion",
];

// A shallow read would only ever see containers, so recurse. Leaves are
// { id, name } pointers; a node with no `name` is a bare wrapper and
// contributes nothing.
export const leafNames = (fields = []) =>
  fields.flatMap((field) => {
    if (CONTAINER_TYPES.includes(field.type)) {
      return leafNames(field.fields || []);
    }
    return field.name ? [field.name] : [];
  });

// The step-bearing node, whether the published layout models steps as a wizard
// (`steps`) or as wizardMode tabs (`tabs`). Both are normalised to one array so
// callers never have to care which the DB served.
//
// This also fixes a real bug: useProposalWizard used to look only for
// `type === "wizard"`, while the layout in play declares `type: "tabs"`. Its
// step list was therefore always empty — isLastStep never went true so Finish
// never appeared, goNext advanced to -1, and per-step validation silently ran
// against an empty name list.
export const findSteps = (nodes = []) => {
  for (const node of nodes) {
    if (node.type === "wizard" && node.steps) return node.steps;
    if (node.type === "tabs" && node.tabs) return node.tabs;
    const nested = findSteps(node.fields || node.children || []);
    if (nested) return nested;
  }
  return null;
};

const useProposalBlueprint = () => {
  const fields = useSelector((state) => state.buyNow.ProposalFieldsJSON);
  const layoutJSON = useSelector((state) => state.buyNow.ProposalLayoutJSON);
  const rulesJson = useSelector((state) => state.buyNow.ProposalRulesJSON);

  // Same unwrap the admin panel does: the column holds the full envelope
  // ({version, form:{key,type,children}}), but FormRenderer wants the children
  // array. The `?? layoutJSON` fallback covers a bare array being stored.
  const layout = useMemo(
    () => layoutJSON?.form?.children ?? layoutJSON,
    [layoutJSON],
  );

  const steps = useMemo(
    () => (Array.isArray(layout) ? findSteps(layout) || [] : []),
    [layout],
  );

  // Leaf field names per step, in step order — used to pick which names to
  // validate when advancing, and to work out which step an invalid field is on.
  const stepFieldNames = useMemo(
    () => steps.map((step) => leafNames(step.fields || [])),
    [steps],
  );

  // name -> label, so error messages can name the offending field.
  const fieldLabels = useMemo(() => {
    if (!Array.isArray(fields)) return {};
    return fields.reduce((acc, field) => {
      if (field.name) acc[field.name] = field.label || field.name;
      return acc;
    }, {});
  }, [fields]);

  // The slice defaults ({} for fields, [] for layout) do not match the shapes
  // FormRenderer needs, and getFormRenderer resolves independently of when this
  // form mounts — so callers hold off until both have actually arrived. Same
  // check the admin panel's ProposalForm makes.
  const isReady = Array.isArray(fields) && Array.isArray(layout);

  return {
    fields,
    layout,
    rulesJson,
    steps,
    stepFieldNames,
    fieldLabels,
    isReady,
  };
};

export default useProposalBlueprint;
